import Style, { type RenderFunction } from 'ol/style/Style';
import { VectorMath } from 'shared/common/domain/vectorMath';
import { Color } from 'shared/common/domain/color';
import { isDebugEnabled } from 'shared/common/debug';
import { messageIcon } from '../domain/icons';
import { parseRichText } from '../../mapStyles/domain/richText';

type RenderState = Parameters<RenderFunction>[1];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MIN_FONT_SIZE_TEXT = 5;
const MIN_FONT_SIZE_ICON = 5;
const ICON_MARGIN = 2;
const FONT_FAMILY = 'sans-serif';

/** Text is measured once at this size and scaled linearly to the size it is drawn at. */
const REFERENCE_FONT_SIZE = 100;

/** Safety cap so caches keyed by label / color can never grow without bounds. */
const MAX_CACHE_ENTRIES = 5000;

/** Enable with the 'rectangleStylePerf' debug flag. Logs one timing table per rendered frame. */
const PERF_DEBUG_FLAG = 'rectangleStylePerf';

/** The debug flag is re-read every this many renderer calls, so the disabled path stays cheap. */
const PERF_CHECK_INTERVAL = 256;

// ---------------------------------------------------------------------------
// Debug / performance metrics
// ---------------------------------------------------------------------------

/**
 * Collects timings and counters for one "burst" of renderer calls (a map frame, or one hit test)
 * and prints them as a single report when the burst is over.
 *
 * OpenLayers renders all features of a frame synchronously, so a microtask queued by the first
 * recorded value runs right after the last feature of that frame has been drawn.
 */
class PerfCollector {
  private readonly timings = new Map<string, { ms: number; calls: number }>();
  private readonly counters = new Map<string, number>();
  private flushScheduled = false;
  private context = '';

  constructor(
    private readonly name: string,
    private readonly totalLabel: string,
    private readonly featuresLabel: string,
    private readonly extra?: () => Record<string, number>,
  ) {}

  add(label: string, ms: number) {
    const entry = this.timings.get(label);
    if (entry) {
      entry.ms += ms;
      entry.calls++;
    } else {
      this.timings.set(label, { ms, calls: 1 });
    }
    this.scheduleFlush();
  }

  count(label: string, n = 1) {
    this.counters.set(label, (this.counters.get(label) ?? 0) + n);
    this.scheduleFlush();
  }

  /** Remembers the view state of the burst, only the first call per burst allocates. */
  noteContext(resolution: number, rotation: number) {
    if (this.context === '') {
      this.context = `resolution ${resolution.toFixed(3)}, rotation ${rotation.toFixed(2)}`;
    }
  }

  private scheduleFlush() {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    queueMicrotask(() => this.flush());
  }

  private flush() {
    const round = (value: number) => Math.round(value * 100) / 100;

    const total = this.timings.get(this.totalLabel)?.ms ?? 0;
    const features = this.counters.get(this.featuresLabel) ?? 0;

    const rows = [...this.timings.entries()]
      .sort((a, b) => b[1].ms - a[1].ms)
      .map(([section, { ms, calls }]) => ({
        section,
        'total ms': round(ms),
        calls,
        'avg µs': round((ms / calls) * 1000),
      }));

    const counters = { ...Object.fromEntries(this.counters), ...(this.extra?.() ?? {}) };
    const context = this.context ? ` (${this.context})` : '';

    console.groupCollapsed(
      `[rectangleStyle] ${this.name}: ${features} features, ${round(total)} ms${context}`,
    );
    console.debug(
      'Sections can be nested and do not add up to the total. Collecting these metrics adds ' +
        'overhead of its own, and performance.now() is coarse, so trust totals more than averages.',
    );
    console.table(rows);
    console.debug('Counters', counters);
    console.groupEnd();

    this.timings.clear();
    this.counters.clear();
    this.context = '';
    this.flushScheduled = false;
  }
}

const cacheSizes = () => ({
  'text layout cache size': textLayoutCache.size,
  'alpha cache size': alphaCache.size,
});

const renderPerf = new PerfCollector('render', 'renderer total', 'features seen', cacheSizes);
const hitPerf = new PerfCollector(
  'hit detection',
  'hit renderer total',
  'hit detection features',
  cacheSizes,
);

/** Set to the active collector while a debug-enabled renderer call is running, otherwise null. */
let activePerf: PerfCollector | null = null;

let perfEnabled = false;
let callsUntilPerfCheck = 0;

/** Cheap enabled check: reads the (possibly expensive) debug flag only every few hundred calls. */
function isPerfEnabled(): boolean {
  if (callsUntilPerfCheck-- <= 0) {
    callsUntilPerfCheck = PERF_CHECK_INTERVAL;
    perfEnabled = isDebugEnabled(PERF_DEBUG_FLAG);
  }
  return perfEnabled;
}

// ---------------------------------------------------------------------------
// Caches
// ---------------------------------------------------------------------------

interface TextRun {
  text: string;
  bold: boolean;
  /** Width of this run at REFERENCE_FONT_SIZE. */
  refWidth: number;
}

interface TextLayout {
  runs: TextRun[];
  /** Total width at REFERENCE_FONT_SIZE. */
  refWidth: number;
  /** Line height (font ascent + descent) at REFERENCE_FONT_SIZE. */
  refHeight: number;
}

const textLayoutCache = new Map<string, TextLayout>();
const alphaCache = new Map<string, Map<number, string>>();

let measureContext: CanvasRenderingContext2D | null | undefined;

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureContext === undefined) {
    measureContext = document.createElement('canvas').getContext('2d');
  }
  return measureContext;
}

/** Parses and measures a label once. All later draws of the same label are pure arithmetic. */
function getTextLayout(label: string): TextLayout {
  const perf = activePerf;

  let layout = textLayoutCache.get(label);
  if (layout) {
    perf?.count('text layout cache hits');
    return layout;
  }

  const t0 = perf ? performance.now() : 0;

  if (textLayoutCache.size >= MAX_CACHE_ENTRIES) textLayoutCache.clear();

  layout = measureTextLayout(label);
  textLayoutCache.set(label, layout);

  if (perf) {
    perf.add('text: parse + measure (layout cache miss)', performance.now() - t0);
    perf.count('text layout cache misses');
  }

  return layout;
}

function measureTextLayout(label: string): TextLayout {
  const ctx = getMeasureContext();
  const runs: TextRun[] = [];
  let bold = false;
  let refWidth = 0;
  let refHeight = 0;

  for (const token of parseRichText(label).tokens) {
    switch (token.type) {
      case 'bold-start':
        bold = true;
        break;

      case 'bold-end':
        bold = false;
        break;

      case 'text': {
        let width: number;
        let height: number;

        if (ctx) {
          ctx.font = `${bold ? 700 : 400} ${REFERENCE_FONT_SIZE}px ${FONT_FAMILY}`;
          const m = ctx.measureText(token.content);
          width = m.width;
          height =
            (m.fontBoundingBoxAscent ?? REFERENCE_FONT_SIZE) + (m.fontBoundingBoxDescent ?? 0);
        } else {
          // No canvas available (should not happen in a browser), fall back to an estimate.
          width = token.content.length * REFERENCE_FONT_SIZE * 0.55;
          height = REFERENCE_FONT_SIZE * 1.15;
        }

        runs.push({ text: token.content, bold, refWidth: width });
        refWidth += width;
        refHeight = Math.max(refHeight, height);
        break;
      }
    }
  }

  return { runs, refWidth, refHeight };
}

/** Color.setAlpha parses the color string every time, so memoize per color and opacity. */
function withAlpha(color: string, opacity: number): string {
  const perf = activePerf;

  let byOpacity = alphaCache.get(color);

  if (!byOpacity) {
    if (alphaCache.size >= MAX_CACHE_ENTRIES) alphaCache.clear();
    byOpacity = new Map();
    alphaCache.set(color, byOpacity);
  }

  let result = byOpacity.get(opacity);

  if (result === undefined) {
    const t0 = perf ? performance.now() : 0;

    result = Color.setAlpha(color, opacity);
    byOpacity.set(opacity, result);

    if (perf) {
      perf.add('color: Color.setAlpha (alpha cache miss)', performance.now() - t0);
      perf.count('alpha cache misses');
    }
  } else {
    perf?.count('alpha cache hits');
  }

  return result;
}

// The camera direction is the same for every feature in a frame, so compute it once per rotation.
let cachedRotation = Number.NaN;
let cameraX = 1;
let cameraY = 0;

function updateCamera(rotation: number) {
  if (rotation === cachedRotation) return;

  const camera = VectorMath.rotate([1, 0], -rotation);
  cameraX = camera[0]!;
  cameraY = camera[1]!;
  cachedRotation = rotation;
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function traceRectangle(
  ctx: CanvasRenderingContext2D,
  p0: number[],
  p1: number[],
  p2: number[],
  p3: number[],
) {
  ctx.beginPath();
  ctx.moveTo(p0[0]!, p0[1]!);
  ctx.lineTo(p1[0]!, p1[1]!);
  ctx.lineTo(p2[0]!, p2[1]!);
  ctx.lineTo(p3[0]!, p3[1]!);
  ctx.lineTo(p0[0]!, p0[1]!);
}

function drawRectangle(coordinates: Parameters<RenderFunction>[0], state: RenderState) {
  const perf = activePerf;
  const feature = state.feature;

  if (feature.get('mode') !== 'rectangle') {
    perf?.count('skipped: not a rectangle');
    return;
  }

  const ring = coordinates[0] as number[][] | undefined;
  if (!ring || ring.length < 4) {
    perf?.count('skipped: invalid geometry');
    return;
  }

  const p0 = ring[0]!;
  const p1 = ring[1]!;
  const p2 = ring[2]!;
  const p3 = ring[3]!;

  const opacity = feature.get('opacity');
  const ctx = state.context;

  perf?.count('rectangles drawn');

  ctx.save();

  // Rectangle
  const tShape = perf ? performance.now() : 0;

  ctx.fillStyle = withAlpha(feature.get('color'), opacity);
  ctx.strokeStyle = withAlpha(feature.get('outlineColor'), opacity);
  ctx.lineWidth = feature.get('outlineSize');

  traceRectangle(ctx, p0, p1, p2, p3);
  ctx.fill();
  ctx.stroke();

  if (perf) perf.add('rectangle: colors + fill + stroke', performance.now() - tShape);

  // Content (icon and label)
  const label = (feature.get('label') as string | undefined) ?? '';
  const baseFontSize = feature.get('fontSize') || 12;
  const scaledFontSize = baseFontSize / state.resolution;

  // Text is never drawn smaller than MIN_FONT_SIZE_TEXT (the fitted size can only shrink),
  // so anything below that threshold can skip all measuring and drawing work.
  const hasIcon = !!feature.get('messageIconVisible') && scaledFontSize >= MIN_FONT_SIZE_ICON;
  const hasText = label !== '' && scaledFontSize >= MIN_FONT_SIZE_TEXT;

  if (hasIcon || hasText) {
    const tContent = perf ? performance.now() : 0;

    drawContent(ctx, state, p0, p1, p2, opacity, label, scaledFontSize, hasIcon, hasText);

    if (perf) perf.add('content: icon + text (total)', performance.now() - tContent);
  } else if (perf) {
    perf.count(
      label !== '' ? 'content skipped: font below minimum' : 'content skipped: nothing to draw',
    );
  }

  ctx.restore();
}

function drawContent(
  ctx: CanvasRenderingContext2D,
  state: RenderState,
  p0: number[],
  p1: number[],
  p2: number[],
  opacity: number,
  label: string,
  scaledFontSize: number,
  hasIcon: boolean,
  hasText: boolean,
) {
  const perf = activePerf;

  // Width vector (p0 -> p1) and height vector (p1 -> p2) as plain numbers, no array allocations.
  const wx = p1[0]! - p0[0]!;
  const wy = p1[1]! - p0[1]!;
  const maxWidth = Math.sqrt(wx * wx + wy * wy);

  const hx = p2[0]! - p1[0]!;
  const hy = p2[1]! - p1[1]!;
  const maxHeight = Math.sqrt(hx * hx + hy * hy);

  if (maxWidth === 0 || maxHeight === 0) {
    perf?.count('content skipped: zero-size rectangle');
    return;
  }

  const uwx = wx / maxWidth;
  const uwy = wy / maxWidth;
  const uhx = hx / maxHeight;
  const uhy = hy / maxHeight;

  const centerX = p0[0]! + (hx + wx) / 2;
  const centerY = p0[1]! + (hy + wy) / 2;

  // Flip the content when the rectangle would otherwise be shown upside down.
  updateCamera(state.rotation);
  const upsideDown = cameraX * uwx + cameraY * uwy < 0;

  let rotation = Math.atan2(uwy, uwx);
  if (upsideDown) rotation += Math.PI;

  // Direction content flows in (horizontal) and is offset in (vertical).
  const contentHx = upsideDown ? uwx : -uwx;
  const contentHy = upsideDown ? uwy : -uwy;
  const contentVx = upsideDown ? uhx : -uhx;
  const contentVy = upsideDown ? uhy : -uhy;

  const textColor = withAlpha(state.feature.get('textColor'), opacity);

  let remainingWidth = maxWidth - ICON_MARGIN;
  let iconAreaWidth = 0;

  // ---- Icon ----
  if (hasIcon) {
    const tIcon = perf ? performance.now() : 0;

    iconAreaWidth = ICON_MARGIN;

    // Start at the right edge when upside down, otherwise at the left edge, then step in by the margin.
    const side = upsideDown ? 1 : -1;
    const iconX = centerX + (side * wx) / 2 - contentHx * ICON_MARGIN;
    const iconY = centerY + (side * wy) / 2 - contentHy * ICON_MARGIN;

    // Scale so the icon follows the font size but always fits inside the rectangle.
    const scale = Math.min(
      scaledFontSize / messageIcon.height,
      remainingWidth / messageIcon.width,
      maxHeight / messageIcon.height,
    );

    const drawX = iconX + contentVx * ((scale * messageIcon.height) / 2);
    const drawY = iconY + contentVy * ((scale * messageIcon.height) / 2);

    ctx.save();
    ctx.translate(drawX, drawY);
    ctx.scale(scale, scale);
    ctx.rotate(rotation);
    ctx.fillStyle = textColor;
    ctx.fill(messageIcon.path);
    ctx.restore();

    remainingWidth -= messageIcon.width * scale;
    iconAreaWidth += messageIcon.width * scale;

    if (perf) {
      perf.add('content: draw icon', performance.now() - tIcon);
      perf.count('icons drawn');
    }
  }

  // ---- Text ----
  if (!hasText) return;

  const layout = getTextLayout(label);
  if (layout.runs.length === 0) {
    perf?.count('text skipped: no text runs');
    return;
  }

  // Size of the text if drawn at the unconstrained font size.
  const sizeFactor = scaledFontSize / REFERENCE_FONT_SIZE;
  const width = layout.refWidth * sizeFactor;
  const height = layout.refHeight * sizeFactor;
  if (width <= 0 || height <= 0) {
    perf?.count('text skipped: zero-size text');
    return;
  }

  // Shrink the text until it fits inside the space left next to the icon.
  const fit = Math.min(remainingWidth / width, maxHeight / height, 1);
  const fontSize = scaledFontSize * fit;

  // Do not render if text becomes less than the minimum font size.
  if (fontSize < MIN_FONT_SIZE_TEXT) {
    perf?.count('text hidden: does not fit at minimum font size');
    return;
  }

  const tText = perf ? performance.now() : 0;

  const drawFactor = fontSize / REFERENCE_FONT_SIZE;
  const drawnWidth = layout.refWidth * drawFactor;

  // Text is centered in the space not used by the icon.
  const textCenterX = centerX - (contentHx * iconAreaWidth) / 2;
  const textCenterY = centerY - (contentHy * iconAreaWidth) / 2;

  ctx.textBaseline = 'middle';
  ctx.fillStyle = textColor;
  ctx.translate(
    textCenterX + (contentHx * drawnWidth) / 2,
    textCenterY + (contentHy * drawnWidth) / 2,
  );
  ctx.rotate(rotation);

  let x = 0;
  let currentBold: boolean | null = null;

  for (const run of layout.runs) {
    if (run.bold !== currentBold) {
      ctx.font = `${run.bold ? 700 : 400} ${fontSize}px ${FONT_FAMILY}`;
      currentBold = run.bold;
    }

    ctx.fillText(run.text, x, 0);
    x += run.refWidth * drawFactor;
  }

  if (perf) {
    perf.add('content: draw text (font + fillText)', performance.now() - tText);
    perf.count('texts drawn');
    perf.count('text runs drawn', layout.runs.length);
  }
}

/** Entry point called by OpenLayers for every feature in every frame. */
const renderRectangle: RenderFunction = (coordinates, state) => {
  if (!isPerfEnabled()) {
    drawRectangle(coordinates, state);
    return;
  }

  const t0 = performance.now();

  activePerf = renderPerf;
  renderPerf.count('features seen');
  renderPerf.noteContext(state.resolution, state.rotation);

  try {
    drawRectangle(coordinates, state);
  } finally {
    activePerf = null;
    renderPerf.add('renderer total', performance.now() - t0);
  }
};

/**
 * Cheap renderer for hit detection (forEachFeatureAtPixel, getFeatures, hasFeatureAtPixel).
 * Only the opaque area matters there, so skip icons, text and the color parsing.
 */
const renderRectangleHitArea: RenderFunction = (coordinates, state) => {
  const t0 = isPerfEnabled() ? performance.now() : -1;

  if (state.feature.get('mode') === 'rectangle') {
    const ring = coordinates[0] as number[][] | undefined;

    if (ring && ring.length >= 4) {
      const ctx = state.context;

      ctx.save();
      ctx.fillStyle = '#000000';
      traceRectangle(ctx, ring[0]!, ring[1]!, ring[2]!, ring[3]!);
      ctx.fill();
      ctx.restore();
    }
  }

  if (t0 >= 0) {
    hitPerf.count('hit detection features');
    hitPerf.noteContext(state.resolution, state.rotation);
    hitPerf.add('hit renderer total', performance.now() - t0);
  }
};

export const rectangleStyle = new Style({
  renderer: renderRectangle,
  hitDetectionRenderer: renderRectangleHitArea,
});